import { useAuthentication, useFeedback } from '@ecosystem/newron-client-lib';
import { actions, feedback, lists, overlays } from '@ecosystem/newron-design-system';
import React, { useCallback, useContext, useEffect } from 'react';

import { UpsellContext } from '../../../src/state';
import { setNotificationMessage } from '../../../src/state/action';
import { NotificationToastType, Product, QualtricsWorkflowIds, UpsellName, UpsellStatus } from '../../common';
import { useQualtricsAPI } from '../../hooks/useQualtricsAPI';
import { UpsellSlideDataType } from '../../hooks/useUpsellAlert';
import './UpsellFeedbackModal.css';
import { postUpsellAnalytics } from '../../services/Market.service';
import { ClientUserGroup } from '../../services/Persona.service';
import { getPageByPath } from '../upsell-feedback/UpsellFeedback';

const UPSELL_FEEDBACK_MODAL_HEADER = 'New data is available for your markets';

const MARKET_REPORTING_TEXT = `
  Take decisions based on the latest monthly data.
  Register your interest today and your account manager will get in touch with you.

  You are currently receiving a reduced frequency of market reporting for:
`;

export const UpsellFeedbackModal = ({
  showModal,
  discoverMoreHandler,
  upsellNudgeDataForSlide,
  clientUserGroupsData,
}: {
  showModal: boolean;
  discoverMoreHandler: (showModal: boolean) => void;
  upsellNudgeDataForSlide?: UpsellSlideDataType[];
  clientUserGroupsData: ClientUserGroup;
}): JSX.Element => {
  const { Modal } = overlays;
  const { Table, GenericCell } = lists;
  const { Button } = actions;

  const { extendEmbeddedData } = useFeedback();
  const { user, getAccessToken } = useAuthentication();
  const { triggerQualtricsWorkflow } = useQualtricsAPI();

  const columnsHeader = {
    cell: '',
    deliveriesPerYear: 'Deliveries per year',
    yourSubscription: 'Your subscription',
    latestAvailableData: 'Latest available data',
  };

  const [showModalLoading, setShowModalLoading] = React.useState(true);

  const { dispatch } = useContext(UpsellContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSurveyButtonClick = async (response: boolean) => {
    const embeddedData = {
      email: user?.email,
      userid: user?.userId,
      market: upsellNudgeDataForSlide?.map(row => row.cell).join(', '),
      country: user?.country,
      userGroupIds: clientUserGroupsData.user_groups.map(group => group.user_group_id).join(','),
      userGroupNames: clientUserGroupsData.user_groups.map(group => group.user_group_name).join(','),
      clientName: clientUserGroupsData.client_name,
      clientId: clientUserGroupsData.client_id,
      urlTokenId: QualtricsWorkflowIds.NORMAL_UPSELL,
    };

    const analyticsData = {
      userId: user?.userId,
      upsellName: UpsellName.MONTHLY_DATA,
      upsellStatus: response ? UpsellStatus.UPSELL_ACTIVATED : UpsellStatus.UPSELL_DECLINED,
    };

    try {
      await triggerQualtricsWorkflow({
        trackingText: response ? UpsellStatus.UPSELL_ACTIVATED : UpsellStatus.UPSELL_DECLINED,
        triggerQualtrics: response,
        embeddedData,
        analyticsData,
        trackingBody: {
          pageName: getPageByPath()?.title || Product.Market, //TODO: fix - Temp setting something in analytics
          cell: upsellNudgeDataForSlide?.map(row => row.cell).join(', '),
        },
      });
      if (response) {
        dispatch(
          setNotificationMessage({
            message: 'Thank you for your request. We will be in touch with more information.',
            type: NotificationToastType.Success,
            timeout: 3000,
          })
        );
      }
    } catch (error) {
      dispatch(
        setNotificationMessage({
          message: 'Something went wrong. Please try again later.',
          type: NotificationToastType.Error,
          timeout: 3000,
        })
      );
    }
  };

  const handlePositiveButtonClick = async () => {
    await eventClick({ title: "Yes, I'm interested" });
  };

  const handleNegativeButtonClick = async () => {
    await eventClick({ title: 'Close' });
  };

  const eventClick = useCallback(
    async button => {
      if (button?.title === "Yes, I'm interested") {
        await handleSurveyButtonClick(true);
      }
      if (button?.title === 'Close') {
        await handleSurveyButtonClick(false);
      }
      discoverMoreHandler(false);
    },
    // disabling to enable triggering event
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [discoverMoreHandler]
  );

  const onCloseModal = () => {
    discoverMoreHandler(false);
  };

  useEffect(() => {
    if (showModal) {
      extendEmbeddedData({
        market: upsellNudgeDataForSlide?.map(row => row.cell).join(', '),
        country: user?.country,
        userGroupIds: clientUserGroupsData.user_groups.map(group => group.user_group_id).join(','),
        userGroupNames: clientUserGroupsData.user_groups.map(group => group.user_group_name).join(','),
        clientName: clientUserGroupsData.client_name,
        clientId: clientUserGroupsData.client_id,
        urlTokenId: QualtricsWorkflowIds.NORMAL_UPSELL,
      });
    }
    // eslint-disable-next-line prettier/prettier
  }, [extendEmbeddedData, user?.country, showModal, upsellNudgeDataForSlide, clientUserGroupsData]);

  const postUpsellAnalyticsData = async () => {
    const analyticsData = {
      userId: user?.userId,
      upsellName: UpsellName.MONTHLY_DATA,
      upsellStatus: UpsellStatus.UPSELL_ENGAGED,
    };

    const token = await getAccessToken();
    await postUpsellAnalytics(token, analyticsData);
  };

  useEffect(() => {
    setShowModalLoading(true);
    if (showModal) {
      if (upsellNudgeDataForSlide && upsellNudgeDataForSlide.length) {
        setShowModalLoading(false);
      }
      postUpsellAnalyticsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsellNudgeDataForSlide, showModal]);

  return (
    <Modal isOpen={showModal} size="lg" onClose={onCloseModal}>
      <Modal.Header>{UPSELL_FEEDBACK_MODAL_HEADER}</Modal.Header>
      <Modal.Body>
        <div className="white-space-wrap">{MARKET_REPORTING_TEXT}</div>
        <div className="upsell-feedback-modal-table" data-test-id="upsell-feedback-modal-table">
          <feedback.DotsLoader data-test-id="upsell-feedback-table-loader" active={showModalLoading} />
          {!showModalLoading ? (
            <Table>
              <Table.Header>
                {Object.values(columnsHeader).map((column, cellIndex) => (
                  <th className="column-header p-3" key={cellIndex} align="right" id={`header-${cellIndex}`}>
                    {column}
                  </th>
                ))}
              </Table.Header>
              <Table.Body>
                {upsellNudgeDataForSlide?.map((row, rowIndex) => (
                  <tr className="border-b border-light table-row" key={rowIndex}>
                    {Object.values(row).map((cell, cellIndex) => (
                      <Table.Cell key={cellIndex}>
                        <GenericCell align={cellIndex === 0 ? 'left' : 'right'}>
                          <span className={cellIndex === 0 ? 'data-column' : ''}>{cell}</span>
                        </GenericCell>
                      </Table.Cell>
                    ))}
                  </tr>
                ))}
              </Table.Body>
            </Table>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer align={'right'}>
        {!showModalLoading && (
          <div data-test-id="qualtrics-buttons">
            <Button testId="qualtrics-negative" mode="secondary" onClick={() => handleNegativeButtonClick()}>
              Close
            </Button>
            <Button testId="qualtrics-positive" onClick={() => handlePositiveButtonClick()}>
              Yes, I'm interested
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};
